import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import { FaInbox } from "react-icons/fa";
import { FiList } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearLabBillingPricing,
  fetchBillingLabs,
  fetchLabBillingPricing,
  generateBillByMonth,
  saveLabBillingPricing,
} from "../../store/superAdmin/labBillingPricingSlice";
import { BILLING_CATEGORIES } from "../../utils/jobCard";
import { canAccessLabPricing } from "../../utils/employeeRoles";
import { downloadGenerateBillWorkbook } from "../../utils/buildGenerateBillWorkbook";
import { useToast } from "../../components/utilities/Toast";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminSoftInput,
  AdminSoftSelect,
  AdminPrimaryButton,
  AdminTableCard,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const fieldLabel =
  "block text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-1.5";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const YEARS = [2024, 2025, 2026, 2027, 2028];

type PricingFilter = "search" | "set" | "unset";

function EmptyPanel({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 px-4 text-center text-[#8A8A85]">
      <FaInbox className="text-2xl text-[#DDDDD8]" />
      <p className="text-sm font-medium text-[#4A4A48]">{title}</p>
      <p className="text-xs">{description}</p>
    </div>
  );
}

const LabBillingPricing = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const {
    labs,
    pricing,
    labsLoading,
    pricingLoading,
    saving,
    generatingBill,
    error,
  } = useAppSelector((state) => state.labBillingPricing);

  const allowed = canAccessLabPricing(user?.role, employee?.role);

  const now = new Date();
  const [labSearch, setLabSearch] = useState("");
  const [pricingFilter, setPricingFilter] = useState<PricingFilter>("search");
  const [selectedToken, setSelectedToken] = useState("");
  const [billingLabName, setBillingLabName] = useState("");
  const [currency, setCurrency] = useState("");
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [billMonth, setBillMonth] = useState(now.getMonth() + 1);
  const [billYear, setBillYear] = useState(now.getFullYear());

  useEffect(() => {
    if (!allowed) return;
    dispatch(fetchBillingLabs());
    return () => {
      dispatch(clearLabBillingPricing());
    };
  }, [dispatch, allowed]);

  useEffect(() => {
    if (!selectedToken) {
      dispatch(clearLabBillingPricing());
      setBillingLabName("");
      setCurrency("");
      setPrices({});
      return;
    }
    dispatch(fetchLabBillingPricing(selectedToken));
  }, [dispatch, selectedToken]);

  useEffect(() => {
    if (!pricing || pricing.lab_token !== selectedToken) return;
    setBillingLabName(pricing.invoice_name || "");
    setCurrency(pricing.currency || "");
    const next: Record<string, string> = {};
    for (const category of BILLING_CATEGORIES) {
      const value = pricing.prices?.[category];
      next[category] =
        value === undefined || value === null ? "0" : String(value);
    }
    setPrices(next);
  }, [pricing, selectedToken]);

  useEffect(() => {
    if (error) showToast(error, "error");
  }, [error, showToast]);

  const pricedCount = useMemo(
    () => labs.filter((lab) => lab.has_pricing).length,
    [labs]
  );
  const unpricedCount = useMemo(
    () => labs.filter((lab) => !lab.has_pricing).length,
    [labs]
  );

  const visibleLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();

    if (pricingFilter === "search") {
      if (!q) return [];
      return labs.filter((lab) =>
        String(lab.token || "").toLowerCase().includes(q)
      );
    }

    let list = labs.filter((lab) =>
      pricingFilter === "set" ? lab.has_pricing : !lab.has_pricing
    );
    if (q) {
      list = list.filter((lab) =>
        String(lab.token || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [labs, labSearch, pricingFilter]);

  const zeroCount = useMemo(() => {
    return BILLING_CATEGORIES.filter((category) => {
      const n = Number(prices[category]);
      return !Number.isFinite(n) || n === 0;
    }).length;
  }, [prices]);

  const onPriceChange = useCallback((category: string, value: string) => {
    setPrices((prev) => ({ ...prev, [category]: value }));
  }, []);

  const handleSave = async () => {
    if (!selectedToken) {
      showToast("Select a lab first", "error");
      return;
    }
    if (!billingLabName.trim() || !currency.trim()) {
      showToast("Lab name and currency are required", "error");
      return;
    }

    const numericPrices: Record<string, number> = {};
    for (const category of BILLING_CATEGORIES) {
      const parsed = Number(prices[category]);
      if (!Number.isFinite(parsed) || parsed < 0) {
        showToast(`Invalid price for ${category}`, "error");
        return;
      }
      numericPrices[category] = Math.round(parsed * 100) / 100;
    }

    try {
      await dispatch(
        saveLabBillingPricing({
          labToken: selectedToken,
          invoice_name: billingLabName.trim(),
          currency: currency.trim(),
          prices: numericPrices,
        })
      ).unwrap();
      showToast("Lab pricing saved", "success");
    } catch (err: any) {
      showToast(String(err || "Failed to save pricing"), "error");
    }
  };

  const handleGenerateBill = async () => {
    try {
      const payload = await dispatch(
        generateBillByMonth({ month: billMonth, year: billYear })
      ).unwrap();
      if (!payload?.labs?.length) {
        showToast(
          "No billed cases found for that month (employee marked delivery units only)",
          "error"
        );
        return;
      }
      await downloadGenerateBillWorkbook(payload);
      showToast(
        `Downloaded ${payload.labCount} lab sheet(s), ${payload.caseCount} case(s)`,
        "success"
      );
    } catch (err: any) {
      showToast(String(err || "Failed to generate bill"), "error");
    }
  };

  const listEmptyTitle =
    pricingFilter === "search" && !labSearch.trim()
      ? "Search by token"
      : pricingFilter === "set"
        ? "No labs with pricing"
        : pricingFilter === "unset"
          ? "No labs without pricing"
          : "No labs found";

  const listEmptyDescription =
    pricingFilter === "search" && !labSearch.trim()
      ? "Enter a lab token to find and edit pricing."
      : pricingFilter === "set" || pricingFilter === "unset"
        ? "Try another filter or search by token."
        : "Try a different token.";

  if (!allowed) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Lab Pricing</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Lab Pricing"
          subtitle="Set per-lab billing category rates, then generate monthly work summaries"
        >
          <Link
            to="/dashboard/admin/lab-pricing/details"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#1A1A18] text-xs font-medium shadow-sm hover:bg-white/90 transition"
          >
            <FiList className="text-sm" />
            All Labs Table
          </Link>
          <AdminSelect
            value={pricingFilter}
            onChange={(v) => setPricingFilter(v as PricingFilter)}
            className="w-full sm:w-[180px]"
          >
            <option value="search">Search by token</option>
            <option value="set">Pricing set ({pricedCount})</option>
            <option value="unset">Pricing not set ({unpricedCount})</option>
          </AdminSelect>
          <AdminSearchInput
            value={labSearch}
            onChange={setLabSearch}
            placeholder="Search labs by token…"
            className="w-full sm:w-[260px]"
          />
        </AdminPageHeader>

        <div className="bg-white rounded-2xl border border-[#E8E8E4] shadow-sm p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-[#1A1A18]">
                Generate Bill
              </h2>
              <p className="text-xs text-[#8A8A85] mt-1 max-w-2xl">
                Builds Apps Script-style work summaries for verified labs in the
                selected month. Uses delivery tooth numbers. Filled-order cases
                expand to one row per zip (same case id, zip name as case name).
                Unmarked units are highlighted red; unit-allocator notes green;
                category “Others” yellow. Uses category prices from Lab Pricing.
                Excludes MS, QF, DC. Element labs (partner elemdig) get the
                common Element Digital sheet plus per-token [ED] sheets. Other
                User accounts that share an invoice name share one sheet, named
                with each token.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3">
              <div>
                <label className={fieldLabel}>Month</label>
                <AdminSoftSelect
                  value={String(billMonth)}
                  onChange={(e) => setBillMonth(Number(e.target.value))}
                  className="min-w-[140px]"
                >
                  {MONTHS.map((name, idx) => (
                    <option key={name} value={idx + 1}>
                      {name}
                    </option>
                  ))}
                </AdminSoftSelect>
              </div>
              <div>
                <label className={fieldLabel}>Year</label>
                <AdminSoftSelect
                  value={String(billYear)}
                  onChange={(e) => setBillYear(Number(e.target.value))}
                  className="min-w-[100px]"
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </AdminSoftSelect>
              </div>
              <AdminPrimaryButton
                type="button"
                onClick={handleGenerateBill}
                disabled={generatingBill}
                className="px-5 py-2.5"
              >
                {generatingBill ? "Generating…" : "Generate Bill"}
              </AdminPrimaryButton>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4">
          <AdminTableCard className="overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E8E8E4]">
              <h2 className="text-sm font-semibold text-[#1A1A18]">Labs</h2>
              <p className="text-xs text-[#8A8A85] mt-0.5">
                {labsLoading
                  ? "Loading…"
                  : pricingFilter === "search" && !labSearch.trim()
                    ? "Search to find a lab"
                    : `${visibleLabs.length} lab${visibleLabs.length === 1 ? "" : "s"}`}
              </p>
            </div>
            <div className="max-h-[70vh] overflow-auto">
              {labsLoading ? (
                <div className="p-4 text-sm text-[#8A8A85]">Loading labs…</div>
              ) : visibleLabs.length === 0 ? (
                <EmptyPanel
                  title={listEmptyTitle}
                  description={listEmptyDescription}
                />
              ) : (
                <ul className="divide-y divide-[#F0F0EC]">
                  {visibleLabs.map((lab) => {
                    const token = String(lab.token || "").toUpperCase();
                    const active = token === selectedToken;
                    const hasPricing = Boolean(lab.has_pricing);
                    return (
                      <li key={token}>
                        <button
                          type="button"
                          onClick={() => setSelectedToken(token)}
                          className={`w-full text-left px-4 py-3 transition ${
                            active
                              ? "bg-[#E8F6F9] border-l-4 border-[#2fa9bf]"
                              : "hover:bg-[#F8F8F6] border-l-4 border-transparent"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-[#1A1A18]">
                              {token}
                            </div>
                            <span
                              className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded-md px-1.5 py-0.5 ${
                                hasPricing
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {hasPricing ? "Set" : "Not set"}
                            </span>
                          </div>
                          <div className="text-xs text-[#4A4A48] truncate">
                            {lab.user_name || "—"}
                          </div>
                          {lab.organisation ? (
                            <div className="text-[11px] text-[#8A8A85] truncate mt-0.5">
                              {lab.organisation}
                            </div>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </AdminTableCard>

          <div className="space-y-4">
            {!selectedToken ? (
              <AdminTableCard>
                <EmptyPanel
                  title="Select a lab"
                  description="Search by token or browse pricing set / not set, then choose a lab."
                />
              </AdminTableCard>
            ) : pricingLoading && !pricing ? (
              <AdminTableCard>
                <div className="p-6 text-sm text-[#8A8A85]">
                  Loading pricing for {selectedToken}…
                </div>
              </AdminTableCard>
            ) : (
              <>
                <div className="bg-white rounded-2xl border border-[#E8E8E4] shadow-sm p-5 sm:p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h2 className="text-base font-semibold text-[#1A1A18]">
                        {selectedToken}
                        {pricing?.lab_name ? ` — ${pricing.lab_name}` : ""}
                      </h2>
                      {pricing?.updated_at ? (
                        <p className="text-xs text-[#8A8A85] mt-0.5">
                          Last updated
                          {pricing.updated_by
                            ? ` by ${pricing.updated_by}`
                            : ""}{" "}
                          on {new Date(pricing.updated_at).toLocaleString()}
                        </p>
                      ) : (
                        <p className="text-xs text-[#8A8A85] mt-0.5">
                          No saved rates yet — defaults shown as 0
                        </p>
                      )}
                    </div>
                    {zeroCount > 0 ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1">
                        {zeroCount} categor{zeroCount === 1 ? "y" : "ies"} at 0
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full">
                      <label className={fieldLabel}>Lab Name</label>
                      <AdminSoftInput
                        value={billingLabName}
                        onChange={(e) => setBillingLabName(e.target.value)}
                        placeholder="Shown as Lab Name on work summary"
                        className="w-full"
                      />
                    </div>
                    <div className="w-full sm:max-w-[200px]">
                      <label className={fieldLabel}>Currency</label>
                      <AdminSoftInput
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        placeholder="USD / INR / EUR"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <AdminTableCard>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px]">
                      <thead>
                        <tr className="border-b border-[#E8E8E4] bg-[#F8F8F6]">
                          <th className={`${adminTableHead} text-center`}>
                            Billing Category
                          </th>
                          <th
                            className={`${adminTableHead} text-center w-[180px]`}
                          >
                            Price / Unit
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {BILLING_CATEGORIES.map((category) => {
                          const isZero =
                            !Number.isFinite(Number(prices[category])) ||
                            Number(prices[category]) === 0;
                          return (
                            <tr
                              key={category}
                              className="border-b border-[#F0F0EC] last:border-0"
                            >
                              <td className={`${adminTableCell} text-center`}>
                                <span className="text-sm text-[#1A1A18]">
                                  {category}
                                </span>
                                {isZero ? (
                                  <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600">
                                    unset
                                  </span>
                                ) : null}
                              </td>
                              <td className={`${adminTableCell} text-center`}>
                                <AdminSoftInput
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={prices[category] ?? "0"}
                                  onChange={(e) =>
                                    onPriceChange(category, e.target.value)
                                  }
                                  className="w-full max-w-[160px] mx-auto text-center"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </AdminTableCard>

                <div className="flex justify-end">
                  <AdminPrimaryButton
                    type="button"
                    onClick={handleSave}
                    disabled={saving || pricingLoading}
                    className="px-6 py-2.5"
                  >
                    {saving ? "Saving…" : "Save Pricing"}
                  </AdminPrimaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      </AdminPageShell>
    </>
  );
};

export default LabBillingPricing;
